import Head from 'next/head';
import { useRouter } from 'next/router';
import metas from '../../config/meta.json';

// Site-wide fallbacks for pages that don't (yet) have a meta.json entry,
// so we never emit an empty <title> or a description-less page.
const DEFAULT_TITLE = 'Krit.ro – Magazin de Jocuri, TCG & Hobby';
const DEFAULT_DESCRIPTION =
    'Cărți TCG, jocuri de societate, Funko și accesorii — plus TCG singles. Comandă online pe Krit.ro cu livrare rapidă în toată țara.';
const DEFAULT_OG_IMAGE = 'https://krit.ro/img/og-logo.a.png';

// Maps a Next.js locale to an Open Graph locale tag.
const OG_LOCALE = { ro: 'ro_RO', en: 'en_US' };

const processTemplate = (template, vars) => {
    if(Object.keys(vars).length === 0) {
        return template;
    }

    for(let i in vars) {
        const regex = new RegExp(`{${i}}`, 'gi');
        template = (template ||'').replace(regex, vars[i]);
    }

    return template;
}

// Canonical/og:url must be a single stable URL per page. `router.asPath`
// carries every filter, sort, and tracking param, which would mint a
// unique self-canonical for each combination (duplicate-content + index
// bloat). Strip the query string; keep only `page` (>1) so paginated
// pages self-canonicalize instead of collapsing onto page 1.
const buildCanonical = (router) => {
    const path = (router.asPath || '/').split('?')[0].split('#')[0];
    const page = parseInt(router.query.page, 10);
    const suffix = page > 1 ? `?page=${page}` : '';
    return `https://krit.ro${path}${suffix}`;
}

const MetaElement = (props) => {
    const pageId = props.pageId;
    const variables = props.variables || {};
    const router = useRouter();

    const meta = metas[pageId] || {};

    const title = processTemplate(meta.title, variables) || DEFAULT_TITLE;
    const description = processTemplate(meta.description, variables) || DEFAULT_DESCRIPTION;
    const ogTitle = processTemplate(meta.ogTitle || meta.title, variables) || title;
    const ogDescription = processTemplate(meta.ogDescription || meta.description, variables) || description;
    // `props.image` lets a page feed its own social image (product photo,
    // card scan) through here, so og:image has a single source and pages
    // don't render a second, duplicate <Head>.
    const ogImage = props.image || processTemplate(meta.ogImage || '', variables) || DEFAULT_OG_IMAGE;
    const ogType = meta.ogType || 'website';
    const canonicalUrl = buildCanonical(router);
    // hreflang cluster. `ro` is the default locale (unprefixed URL); `en`
    // lives under `/en`. x-default points at the Romanian (default) URL.
    const enUrl = canonicalUrl.replace('https://krit.ro', 'https://krit.ro/en');
    const ogLocale = OG_LOCALE[router.locale] || OG_LOCALE.ro;

    return (
        <Head>
            <title>{title}</title>
            <meta name="description" content={description}/>
            {props.noindex && <meta name="robots" content="noindex, follow" />}

            {/* Open Graph tags. og:image dimensions are intentionally omitted —
                product/card images aren't a fixed 1200x630, and asserting a
                wrong size makes scrapers mis-crop. They fetch the real image. */}
            <meta property="og:site_name" content="Krit.ro" />
            <meta property="og:locale" content={ogLocale} />
            <meta property="og:title" content={ogTitle}/>
            <meta property="og:description" content={ogDescription}/>
            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:image" content={ogImage} key="og:image"/>
            <meta property="og:image:alt" content={ogTitle} key="og:image:alt"/>

            {/* Twitter Card tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={ogTitle}/>
            <meta name="twitter:description" content={ogDescription}/>
            <meta name="twitter:image" content={ogImage} key="twitter:image"/>

            {/* Canonical URL */}
            <link rel="canonical" href={canonicalUrl} />

            {/* hreflang alternates */}
            <link rel="alternate" hrefLang="ro-RO" href={canonicalUrl} />
            <link rel="alternate" hrefLang="en" href={enUrl} />
            <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        </Head>
    )
}

export default MetaElement;
